import { Flex, Typography } from '@iheartjane/reefer';
import { Form, useFormContext } from '@iheartjane/reefer-hook-form';
import { useEffect } from 'react';
import { useWatch } from 'react-hook-form';

import { useSpecialsModal } from '@jane/business-admin/providers/SpecialsModalProvider';

export const DiscountTypeAndDiscountAmountField = ({
  isCreateMode = false,
}: {
  isCreateMode: boolean;
}) => {
  const {
    posSyncMap: { isJanePosSynced, posSynced },
    isReadOnly,
  } = useSpecialsModal();
  const { getValues, trigger } = useFormContext();
  const discountTypeValue = getValues('discount_type');
  const specialTypeValue = getValues('special_type');
  const discountAmountValue = getValues(`discount_${discountTypeValue}`);

  const specialTypeWatch = useWatch({ name: 'special_type' });
  const specialType = specialTypeWatch || specialTypeValue;

  const hasTargetPriceOption = [
    'product',
    'bundle',
    'bulk_pricing',
    'spending_threshold',
  ].includes(specialType);
  const discountTypeWatch = useWatch({ name: 'discount_type' });

  const isBulkPricing =
    (specialTypeWatch || specialTypeValue) === 'bulk_pricing';
  const isPercentOrDollar = ['percent', 'dollar_amount'].includes(
    discountTypeWatch || discountTypeValue
  );
  const discountAmountLabel =
    isBulkPricing && isPercentOrDollar
      ? 'Discount amount per product'
      : 'Discount amount';

  useEffect(() => {
    // Need to run validation if the user selects Dollars or Target Price first
    // then enters in a value > 100 and switches to Percentage
    if (discountTypeValue === 'percent') trigger('discount_percent');
  }, [discountTypeValue, trigger]);

  const discountTypes = [
    { label: 'Percentage (%)', value: 'percent' },
    { label: 'Dollars ($)', value: 'dollar_amount' },
    ...(hasTargetPriceOption
      ? [{ label: 'Target Price', value: 'target_price' }]
      : []),
  ];

  const showField =
    isCreateMode || ((isJanePosSynced || !posSynced) && !isReadOnly);

  return (
    <Flex gap={24} justifyContent="space-between" width="100%">
      {showField ? (
        <Form.SelectField
          name="discount_type"
          label="Discount type"
          helperText={
            hasTargetPriceOption
              ? 'Target price applies to entire discounted group, not each item'
              : ''
          }
          options={discountTypes}
          required
          width="50%"
        />
      ) : (
        <Flex width="50%" flexDirection="column">
          <Typography color="grays-black" variant="body-semibold">
            Discount type
          </Typography>
          <Typography color="grays-black" mt={4} as="p">
            {
              discountTypes.find((type) => type.value === discountTypeValue)
                ?.label
            }
          </Typography>
        </Flex>
      )}
      {showField ? (
        <Form.NumberField
          name={`discount_${discountTypeValue}`}
          label={discountAmountLabel}
          required
          width="50%"
          min={0}
          startUnit={
            ['dollar_amount', 'target_price'].includes(discountTypeValue)
              ? '$'
              : undefined
          }
          endUnit={discountTypeValue === 'percent' ? '%' : undefined}
          validate={(value: any) => {
            return discountTypeValue === 'percent' && value > 100
              ? 'Discount amount cannot exceed 100'
              : true;
          }}
          disableOnWheel
        />
      ) : (
        <Flex width="50%" flexDirection="column">
          <Typography color="grays-black" variant="body-semibold">
            {discountAmountLabel}
          </Typography>
          <Typography color="grays-black" mt={4}>
            {discountTypeValue === 'percent'
              ? `${discountAmountValue}%`
              : `$${discountAmountValue}`}
          </Typography>
        </Flex>
      )}
    </Flex>
  );
};
